import * as y from "yup"
import type {ClassicCard as BrazeContentCard} from "@braze/web-sdk"
import {ContentCardID, idSchema} from "./components/base"
import {Popup, PopupSchema} from "./components/popup"
import {Banner, BannerSchema} from "./components/banner"
import {Trigger, TriggerSchema} from "./components/trigger"
import {filterMessages} from "./components/message"

export type {Banner, Popup, Trigger, ContentCardID}
export {filterMessages}

/**
 * The Braze content card, i.e a notification as it comes from braze sdk.
 *
 * Additional methods are removed, converting this from a class like object to a dumb data object
 */
export type SimpleBrazeContentCard = Omit<
  BrazeContentCard,
  "id" | "removeSubscription" | "removeAllSubscriptions" | "subscribeToClickedEvent" | "subscribeToDismissedEvent" | "dismissCard"
> & {id: string}

/**
 * these are limited based on what Native currenly suports
 * */
export const ContentCardScreen = {
  home: "home",
  perks: "perks/discover",
  rewards: "rewards/categories",
  celebration: "recognition/polls",
  wellness: "wellness"
} as const
export type ContentCardScreen = (typeof ContentCardScreen)[keyof typeof ContentCardScreen]

export const ContentCardPosition = {
  top: "top"
} as const

export type PBEnhancedExtras = Prettify<{
  id: string
  component_id: ContentCardID
  showOnPages?: string[]
  priority: number
  banner?: Banner
  popup?: Popup
  trigger?: Trigger
}>

/** TODO create type that has removed properties gone */
export type PBEnhancedContentCard = Prettify<
  Omit<SimpleBrazeContentCard, "extras"> & {
    extras: PBEnhancedExtras
  }
>

type KnownCard<Card extends ContentCardID> = Prettify<
  Omit<PBEnhancedContentCard, "extras"> & {extras: WithRequired<PBEnhancedExtras, Card>}
>

/** ContentCard with a banner component */
export type ContentCardBanner = KnownCard<"banner">

/** ContentCard with a popup component */
export type ContentCardPopup = KnownCard<"popup">

/** ContentCard with a trigger component */
export type ContentCardTrigger = KnownCard<"trigger">

/** ContentCard with a message component */
export type ContentCardMessage = KnownCard<"message">

export type ContentCard = PBEnhancedContentCard

export type LegacyNotificationCard = SimpleBrazeContentCard

/**
 * shared validation fields for both legacy notifications and content cards
 * no doubt there are methods to compose schemas, but I have been unable to
 * find them on our current version of yup
 **/
const sharedFields = {
  aspectRatio: y.number().required(),
  categories: y.array(y.string()),
  clicked: y.boolean().required(),
  description: y.string().required(),
  dismissed: y.boolean().required(),
  dismissible: y.boolean().required(),
  expiresAt: y.date().required(),
  id: y.string().required(),
  imageUrl: y.string().nullable(true),
  linkText: y.string().nullable(true),
  pinned: y.boolean().required(),
  title: y.string().required(),
  updated: y.date().required(),
  url: y.string().nullable(true),
  viewed: y.boolean().required()
}

export const SimpleBrazeContentCardSchema = y.object<SimpleBrazeContentCard>({
  ...sharedFields,
  extras: y.mixed().nullable(true).default(null),
  isControl: y.boolean().default(false)
})

export const ContentCardSchema = y.object<ContentCard>({
  ...sharedFields,
  extras: y.object<PBEnhancedExtras>({
    id: y.string().required(),
    component_id: idSchema,
    showOnPages: y.array(y.string()),
    priority: y.number().default(0),
    banner: BannerSchema.nullable(true).default(null),
    popup: PopupSchema.nullable(true).default(null),
    trigger: TriggerSchema.nullable(true).default(null)
  }),
  isControl: y.boolean().default(false)
})
